/**
 * Autism (ASD) triage decision model (frontend).
 *
 * Drives the autism triage decision wizard (AutismTriageDecisionForm),
 * restructured per the clinical lead's review of the shared ADHD-shaped
 * wizard (July 2026):
 *   Step 1        — Evidence against the DSM-5 structure: criterion A split
 *                   into A1-A3, criterion B into B1-B4 (at least two), plus
 *                   criteria C (early developmental period) and D (functional
 *                   impairment). Criterion E and the severity levels are
 *                   deliberately excluded at triage. Each A/B criterion also
 *                   records who reports it (the pervasiveness proxy) and
 *                   whether it is long-standing.
 *   Steps 2-9     — Eight other-factor groups: medical/medication/development,
 *                   sensory (its own step), cognition & learning, mental
 *                   health & wellbeing, safeguarding, family/home/lifestyle,
 *                   education & attendance, and support.
 *   Step 10       — Risks plus unresolved priority problems (the gating point).
 *   Step 11       — Conclusion (free text + Accept), prompts structured around
 *                   the pervasiveness / alternative-explanations triage logic.
 *   Step 12       — Signposting.
 *
 * Criterion wording below follows the structure of DSM-5 in close paraphrase;
 * swapping in the verbatim manual text is pending a licensing check.
 *
 * Keys are stable — the backend validates against AUTISM_EVIDENCE_KEYS /
 * AUTISM_FACTOR_GROUP_KEYS in rms_forms.py and the outcome-letter input
 * builder maps them to labels in rms_llm_inputs.py; keep them in sync.
 *
 * The structured answers, clinician-applied evidence tags and surfaced data
 * are decision support and inputs to the letter-paragraph drafts; they never
 * auto-compute or gate the decision — the clinician's Accept / Not accepted
 * choice is the decision.
 */

// The graded evidence scale is shared with ADHD (No / Partial / Clear /
// Inconclusive) — re-exported so the wizard reads one scale regardless of
// pathway.
export { EVIDENCE_SCALE, EVIDENCE_SCALE_IDS, NOT_CAPTURED_LABEL } from './adhdTriageOutcomes';

// ── Step 1: DSM-5 evidence ────────────────────────────────────────────────────

export const AUTISM_EVIDENCE_INTRO =
  'The criteria below follow the DSM-5 structure. Judge each criterion against '
  + "the child's developmental age, not their chronological age. DSM-5 severity "
  + 'levels (1-3) and criterion E are deliberately not included at triage.';

// Shown when the referral answers indicate an EHCP or educational psychologist
// involvement (LSC-018 interim, RMS-199): those documents are not held in RMS,
// so the clinician is reminded to review them outside the system. Wording
// crosses the clinical correctness gate before release.
export const AUTISM_EXTERNAL_EVIDENCE_NOTE_TITLE =
  'Documents to review outside this system';

export const AUTISM_EXTERNAL_EVIDENCE_NOTE =
  'The referral answers indicate there may be an EHCP or educational '
  + 'psychologist report for this child. Those documents are not held in this '
  + 'system - obtain and review them alongside the referral evidence, and note '
  + 'relevant findings in your comments.';

/**
 * Per-criterion impact scale (TD-241): the A/B criteria are rated on
 * pervasiveness / functional impact rather than the graded evidence scale, so a
 * long-standing-but-narrow trait is not over-weighted. Three points, no
 * "inconclusive" — the clinician then makes the threshold decision below.
 */
export const AUTISM_IMPACT_SCALE = [
  { id: 'limited_impact', label: 'Limited impact' },
  { id: 'partial_impact', label: 'Partial impact' },
  { id: 'significant_impact', label: 'Significant impact' },
];

export const AUTISM_IMPACT_SCALE_IDS = AUTISM_IMPACT_SCALE.map((s) => s.id);

/**
 * Forced per-criterion threshold decision (TD-241): after rating impact, the
 * clinician records a yes/no on each A/B criterion so triage mimics the
 * end-of-pathway panel and does not sit on the fence. This is the clinician's
 * recorded judgement — the system never computes it or derives an outcome from
 * the set of answers.
 */
export const AUTISM_THRESHOLD_QUESTION =
  'On the balance of probabilities, is there sufficient evidence at threshold to '
  + 'warrant further autism assessment for this criterion?';

/** Shared yes/no option set for the threshold decision and criteria C and D. */
export const AUTISM_YES_NO_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];

export const AUTISM_YES_NO_VALUES = AUTISM_YES_NO_OPTIONS.map((o) => o.id);

/** Section framing for the A and B criterion groups. */
export const AUTISM_CRITERIA_SECTIONS = [
  {
    id: 'A',
    heading: 'A. Social communication and social interaction',
    stem:
      'Persistent deficits in social communication and social interaction across '
      + 'multiple contexts, as manifested by all of the following, currently or by history.',
    note: 'The A criteria carry the diagnostic weight.',
  },
  {
    id: 'B',
    heading: 'B. Restricted, repetitive patterns of behaviour, interests, or activities',
    stem:
      'Restricted, repetitive patterns of behaviour, interests, or activities, as '
      + 'manifested by at least two of the following, currently or by history.',
    note:
      'B-type presentations — especially sensory — have multiple non-autism causes.',
  },
];

/**
 * The seven A/B criteria. Each is rated on EVIDENCE_SCALE (required) and also
 * records who reports it (multi-select) and whether it is long-standing, with
 * an optional comment. `code` is the short label used by the evidence-tagging
 * widget and the tagged-evidence lists.
 */
export const AUTISM_DSM5_CRITERIA = [
  {
    key: 'criterionA1',
    section: 'A',
    code: 'A1',
    label: 'A1 — Deficits in social-emotional reciprocity',
    examples:
      'For example: an unusual social approach, difficulty with back-and-forth '
      + 'conversation, reduced sharing of interests or emotions, or difficulty '
      + 'initiating or responding to social interactions.',
  },
  {
    key: 'criterionA2',
    section: 'A',
    code: 'A2',
    label: 'A2 — Deficits in nonverbal communicative behaviours used for social interaction',
    examples:
      'For example: poorly integrated verbal and nonverbal communication, '
      + 'differences in eye contact and body language, difficulty understanding or '
      + 'using gestures, or limited facial expression.',
  },
  {
    key: 'criterionA3',
    section: 'A',
    code: 'A3',
    label: 'A3 — Deficits in developing, maintaining, and understanding relationships',
    examples:
      'For example: difficulty adjusting behaviour to suit different social '
      + 'contexts, difficulty sharing imaginative play or making friends, or an '
      + 'absence of interest in peers.',
  },
  {
    key: 'criterionB1',
    section: 'B',
    code: 'B1',
    label: 'B1 — Stereotyped or repetitive motor movements, use of objects, or speech',
    examples:
      'For example: simple motor stereotypies, lining up toys, or echoed or '
      + 'idiosyncratic speech.',
  },
  {
    key: 'criterionB2',
    section: 'B',
    code: 'B2',
    label:
      'B2 — Insistence on sameness, inflexible adherence to routines, or ritualised '
      + 'patterns of verbal or nonverbal behaviour',
    examples:
      'For example: extreme distress at small changes, difficulty with transitions, '
      + 'rigid thinking, or needing to take the same route or eat the same food.',
  },
  {
    key: 'criterionB3',
    section: 'B',
    code: 'B3',
    label: 'B3 — Highly restricted, fixated interests that are abnormal in intensity or focus',
    examples:
      'For example: a strong attachment to or preoccupation with unusual objects, '
      + 'or excessively narrow, persistent interests.',
  },
  {
    key: 'criterionB4',
    section: 'B',
    code: 'B4',
    label:
      'B4 — Hyper- or hypo-reactivity to sensory input or unusual interest in '
      + 'sensory aspects of the environment',
    examples:
      'For example: apparent indifference to pain or temperature, adverse response '
      + 'to specific sounds or textures, excessive smelling or touching of objects, '
      + 'or visual fascination with lights or movement.',
  },
];

/** Criterion C — onset, with conditional follow-up prompts either way. The
 * term "masking" is deliberately not used (clinical-lead instruction). */
export const AUTISM_CRITERION_C = {
  key: 'earlyDevelopmentalPeriod',
  heading: 'C. Onset in the early developmental period',
  label: 'Were these traits present in the early developmental period?',
  earlyKnownPrompt:
    'If traits were present early: were they seen by a child development centre or '
    + 'known to health services at the time? If so, why was an assessment not pursued?',
  lateRecognitionPrompt:
    'If traits have only been recognised recently: why do you believe this is? For '
    + 'example, social demands did not exceed the child’s capacity earlier (such as '
    + 'before the complexity of secondary school), or strategies were learned later in life.',
};

/** Criterion D — functional impairment. */
export const AUTISM_CRITERION_D = {
  key: 'functionalImpairment',
  heading: 'D. Functional impairment',
  label:
    'Do the traits cause clinically significant impairment in social, school, or '
    + 'other important areas of current functioning?',
  helper: 'Difficulty without impairment does not meet the threshold.',
};

/** All step-1 evidence keys, in display order (the backend's AUTISM_EVIDENCE_KEYS). */
export const AUTISM_EVIDENCE_KEYS = [
  ...AUTISM_DSM5_CRITERIA.map((c) => c.key),
  AUTISM_CRITERION_C.key,
  AUTISM_CRITERION_D.key,
];

/** Who reports a criterion — the practical pervasiveness proxy. */
export const AUTISM_REPORTED_BY_OPTIONS = [
  { id: 'school', label: 'School' },
  { id: 'home', label: 'Home' },
  { id: 'other', label: 'Other setting' },
  { id: 'not_reported', label: 'Not reported' },
];

export const AUTISM_REPORTED_BY_QUESTION = 'Who reports this? (Select all that apply)';

/** Whether a criterion is long-standing rather than recent-only. */
export const AUTISM_LONG_TERM_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
  { id: 'unclear', label: 'Unclear' },
];

export const AUTISM_LONG_TERM_QUESTION =
  'Is it long-standing — present for an extended period, not only recently?';

// ── Evidence tagging (clinician-applied, never automated) ─────────────────────
// While reviewing the referral evidence the clinician can tag an answer as
// relating to one of the A/B criteria, recording where the evidence came from.
// Tags surface under the matching criterion on step 1 and persist on the
// decision record; the system never maps evidence to criteria itself.

export const AUTISM_TAG_SOURCE_OPTIONS = [
  { id: 'school', label: 'School' },
  { id: 'home', label: 'Home or parent' },
  { id: 'other', label: 'Other' },
];

/** Criterion options for the tag widget: the seven A/B criteria by code. */
export const AUTISM_TAG_CRITERION_OPTIONS = AUTISM_DSM5_CRITERIA.map((c) => ({
  key: c.key,
  code: c.code,
  label: c.label,
}));

// ── Steps 2-9: other-factor groups ────────────────────────────────────────────
// Each factor row surfaces mapped form answers (otherFactorsSurfacing) and
// otherwise renders "Not captured — clinician to note" with a per-factor note
// affordance. `prompts` render as guidance under the factor label; factors
// flagged `supportsFollowThrough` also record an offered-vs-followed-through
// status. Safeguarding and education rows largely await referral-form
// questions, so they surface nothing yet and rely on the note fallback.

export const AUTISM_FOLLOW_THROUGH_OPTIONS = [
  { id: 'not_offered', label: 'Not offered' },
  { id: 'offered_not_taken_up', label: 'Offered but not taken up' },
  { id: 'started_not_completed', label: 'Started but not completed' },
  { id: 'completed_not_in_place', label: 'Completed but not put in place' },
  { id: 'completed_in_place', label: 'Completed and in place' },
  { id: 'unclear', label: 'Unclear' },
];

export const AUTISM_FOLLOW_THROUGH_QUESTION =
  'If support was offered: was it taken up, completed, and put in place?';

export const AUTISM_OTHER_FACTOR_GROUPS = [
  {
    id: 'medicalDevelopment',
    label: 'Medical, medication & development',
    shortLabel: 'Medical & development',
    // Order per the clinical lead's section review (LSC-042 Section 2):
    // developmental history first, the medication block last — below the
    // hearing and vision items ("hearing and vision are part of development").
    factors: [
      {
        factorKey: 'developmentalDelayHistory',
        label: 'History of developmental delay',
        prompts: [
          'Did they have developmental delay prior to starting school?',
          'Which skills or milestones were delayed?',
          'Were they seen at any point by a child development centre?',
          'Were they assessed as having delay by health services?',
        ],
      },
      {
        factorKey: 'speechLanguageSeparate',
        label:
          'Additional or separate speech and language difficulties '
          + '(separate to social communication and interaction)',
      },
      { factorKey: 'hearingOrVision', label: 'Hearing or vision' },
      {
        factorKey: 'medications',
        label: 'Medications',
        prompts: ['Include current medications and non-prescription supplements.'],
      },
    ],
  },
  {
    id: 'sensory',
    label: 'Sensory',
    shortLabel: 'Sensory',
    helper:
      'Sensory needs drive much of the behaviour seen and warrant their own review. '
      + 'Sensory presentations alone are weak discriminators for autism — most '
      + 'children with sensory-only presentations do not have autism.',
    factors: [
      { factorKey: 'sensoryNeedBehaviour', label: 'Sensory-need-driven behaviour' },
    ],
  },
  {
    id: 'cognitionLearning',
    label: 'Cognition & learning',
    shortLabel: 'Cognition & learning',
    footnote:
      'Where a learning disability is present, the step 1 evidence must be judged '
      + 'against the child’s developmental age, not chronological age — the '
      + 'threshold changes.',
    factors: [
      {
        factorKey: 'learningDisability',
        label:
          'Learning disability (global developmental delay, intellectual '
          + 'developmental disorder, global learning issues)',
      },
      {
        factorKey: 'specificLearningDifferences',
        label:
          'Specific learning differences (e.g. dyslexia, dyspraxia, dyscalculia, '
          + 'working memory difficulties, visual processing, auditory processing)',
        prompts: [
          'For each condition identified: who diagnosed it? When? Was the diagnosis '
          + 'formal or informal? NHS or private?',
        ],
      },
      {
        // LSC-042 Section 4: granular EHCP status, who applied, and the far
        // more common non-statutory plans (IEPs) alongside.
        factorKey: 'ehcpStatus',
        label:
          'EHCP status — current plan, planned, application in progress, or '
          + 'previous application',
        prompts: [
          'Who made the application — the school or the parent? Parent-made '
            + 'applications can signal that the school does not support it.',
          'Non-statutory support plans (IEPs) are roughly three times as common '
            + 'as EHCPs — record any in place, current or previous.',
        ],
      },
      {
        factorKey: 'cognitiveFunctioning',
        label:
          'Cognitive functioning / current learning need (degree of delay, current '
          + 'programme of support)',
        prompts: [
          'Include high intelligence / above-average attainment — one of the most '
            + 'common factors on the pathway, and itself a possible cause of '
            + 'atypical social communication.',
        ],
      },
    ],
  },
  {
    id: 'mentalHealthWellbeing',
    label: 'Mental health & wellbeing',
    shortLabel: 'Mental health',
    factors: [
      {
        factorKey: 'mentalHealthDiagnosed',
        label: 'Mental health — explicit or diagnosed mental health problems',
        prompts: [
          'This could include a diagnosis of schizophrenia, psychosis, or an anxiety '
          + 'disorder, or being under a mental health service or team. Who, when, where?',
        ],
      },
      {
        factorKey: 'mentalWellbeingDifficulties',
        label: 'Mental wellbeing — difficulties, including any support accessed',
        prompts: [
          'For example: general anxiety, social anxiety, separation anxiety, low '
          + 'mood, oppositional behaviour, aggression.',
        ],
      },
      {
        factorKey: 'adverseChildhoodExperiences',
        label: 'Adverse childhood experiences (ACEs)',
        prompts: [
          'For example: witness to or experience of domestic violence, bereavement, '
          + 'parental separation, drugs or alcohol in the home, social care involvement.',
        ],
      },
    ],
  },
  {
    id: 'safeguarding',
    label: 'Safeguarding',
    shortLabel: 'Safeguarding',
    footnote:
      'The referral form does not yet capture all of these items; rows fall back to '
      + 'the clinician note until the corresponding form questions are added.',
    factors: [
      {
        factorKey: 'safeguardingActiveInvolvement',
        label: 'Is there any active safeguarding involvement?',
      },
      {
        factorKey: 'safeguardingS47History',
        label:
          'Has there ever been a Section 47 (child protection) enquiry — a formal '
          + 'S47 investigation or meeting stood up?',
      },
      {
        factorKey: 'safeguardingPlans',
        label:
          'Is the child currently on a Child in Need plan or Child Protection plan? '
          + 'Any previous history of either?',
      },
      {
        factorKey: 'safeguardingExploitationRisk',
        label:
          'Risk of grooming or child exploitation (sexual or criminal) — any '
          + 'involvement with local exploitation schemes or panels?',
      },
      {
        factorKey: 'safeguardingRadicalisationRisk',
        label: 'Risk of radicalisation — any Prevent referral or involvement?',
      },
    ],
  },
  {
    id: 'familyHome',
    label: 'Family, home & lifestyle',
    shortLabel: 'Family & home',
    factors: [
      {
        factorKey: 'boundariesParenting',
        label:
          'Boundaries / parenting — has a parenting intervention (per NICE guidance) '
          + 'been offered?',
        prompts: [
          'Consider the local offer for the family’s locality (e.g. Riding the '
          + 'Rapids, Positive Parenting Programme).',
        ],
        supportsFollowThrough: true,
      },
      {
        factorKey: 'screenTimeGaming',
        label: 'Screen time / gaming',
        prompts: [
          'How much time do they usually spend on screens (a screen of any form)?',
          'Do they have a screen of any form in their bedroom?',
          'Have you had challenges limiting or rationing their screen or internet use?',
          'Do they play games with in-game purchase features? Any concerns about money spent?',
        ],
      },
      { factorKey: 'substanceMisuse', label: 'Substance misuse' },
      { factorKey: 'homeLivingConditions', label: 'Home living conditions' },
    ],
  },
  {
    id: 'education',
    label: 'Education & attendance',
    shortLabel: 'Education',
    factors: [
      {
        factorKey: 'schoolNonAttendance',
        label:
          'School non-attendance / emotionally based school avoidance (EBSA)',
      },
      {
        factorKey: 'schoolMoves',
        label: 'School moves / placement changes — how many, when, and why?',
        prompts: [
          'Repeated moves and extended periods out of school can themselves be traumatic.',
        ],
      },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    shortLabel: 'Support',
    helper:
      'For every support item record both what was offered and what was followed '
      + 'through — much is offered but never taken up, completed, or put in place.',
    factors: [
      {
        factorKey: 'previousSupportAccessed',
        label:
          'Support previously accessed — professionals involved, services, activities',
        supportsFollowThrough: true,
      },
      {
        factorKey: 'educationSupport',
        label:
          'Support currently in place at education — SEN support, IEP, statutory '
          + '(EHCP) provision, counselling: precisely what?',
        supportsFollowThrough: true,
      },
    ],
  },
];

export const AUTISM_FACTOR_GROUP_IDS = AUTISM_OTHER_FACTOR_GROUPS.map((g) => g.id);

// ── Step 10: risks & priority problems ────────────────────────────────────────
// Reframed per the 6 Aug 2026 BTH review: the section is about risks and
// barriers to assessment (factors that could make an assessment inappropriate
// right now), kept clearly separate from priority problems, with a labelled
// note for necessary interventions. Wording crosses the clinical correctness
// gate before release.

export const AUTISM_RISKS_SECTION_TITLE = 'Risks and barriers to assessment';

export const AUTISM_RISKS_SECTION_HINT =
  'Risks to the child and factors that could make an autism assessment '
  + 'inappropriate right now. Record these separately from the priority '
  + 'problems below.';

export const AUTISM_RISKS_QUESTION_LABEL =
  'Are there risks present that need reducing first, before an autism assessment '
  + 'baseline can be established?';

export const AUTISM_RISKS_INTERVENTIONS_LABEL = 'Necessary interventions (optional)';

export const AUTISM_RISKS_INTERVENTIONS_HINT =
  'What needs to be in place or resolved before an assessment can go ahead.';

export const AUTISM_PRIORITY_SECTION_HINT =
  'Family and clinical priorities often differ, for example a diagnosis to '
  + 'support an EHCP versus school placement or mental health support. Record '
  + 'priority problems here, separately from the risks above.';

export const AUTISM_PRIORITY_PROBLEMS_QUESTION_LABEL =
  'Are there unresolved priority problems impacting the child, family life, or '
  + 'access to education?';

export const AUTISM_PRIORITY_PLANS_QUESTION_LABEL =
  'If yes: are there currently plans in place to address them?';

export const AUTISM_PRIORITY_PLANS_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'partially', label: 'Partially' },
  { id: 'no', label: 'No' },
];

export const AUTISM_PRIORITY_DETAIL_LABEL =
  'What are the current plans? (Or note that none exist)';

export const AUTISM_RISKS_GATING_NOTE =
  'A serious active risk with no plan must be addressed in its own right — '
  + 'referral for autism assessment is not the plan for it.';

// ── Step 11: conclusion ───────────────────────────────────────────────────────

export const AUTISM_CONCLUSION_HELPER_PROMPTS = [
  'Strengths observed',
  'Difficulties observed',
  'The profile across A1-A3 and B1-B4 — a mixed or partial profile should prompt '
    + 'consideration of alternative explanations (learning disability or specific '
    + 'learning differences, trauma/ACEs, mental health, sensory needs alone, school '
    + 'non-attendance or repeated school moves, very high learning potential)',
  'Pervasiveness — multiple contexts, most of the time, and long-term',
  'Functional impairment',
  'Judged against developmental age, not chronological age',
  'Is an autism assessment appropriate at this time, and why or why not?',
  'Recommended course of action',
];

export const AUTISM_ACCEPT_QUESTION_LABEL = 'Accept onto the autism assessment pathway?';
